/* eslint-disable @nx/enforce-module-boundaries */
import {
  Box,
  Divider,
  Flex,
  HStack,
  Text,
  useColorMode,
  Wrap,
} from '@chakra-ui/react';
import {
  Combobox,
  ComboboxProps,
  OptionType,
  Select,
} from '@gamma/ui/form-fields';
import CalendarTodaySharpIcon from '@gamma/ui/icons/CalendarTodaySharpIcon';
import { ApexOptions } from 'apexcharts';
import moment from 'moment';
import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Chart from 'react-apexcharts';
import { i18n } from '../../../../../../../../../shared/localization';
import { OperationVariables } from '@apollo/client';
import { FetchMoreFunction } from '@apollo/client/react/hooks/useSuspenseQuery';
import { FlushPanelContent } from '@gamma/ui/layout';
import { Alert } from '@gamma/ui/overlay';
import {
  IGetChartTimelineFilters,
  IGetDetectionTimelineSummary,
} from '../../../../../../../queries';
import { chartOptions } from './chartOptions';
import { seriesData } from './seriesData';
import { IGetEntityTimelineSummary } from '@investigator/features/entities/queries';

const { correlationChart } = i18n.pages.detections;
const { detections } = i18n.pages.detections.detection;
const { ariaLabelForTimelineChart } = i18n.pages.detections.correlationChart;
const {
  intervals,
  headings,
  chartDescription,
  abbrevChartDescription,
  detectionTimelineInterval,
} = correlationChart;

const intervalOptions: { label: string; value: string }[] = [
  {
    label: intervals.twentyFourHours,
    value: '86400',
  },
  {
    label: intervals.eightHours,
    value: '28800',
  },
  {
    label: intervals.oneHour,
    value: '3600',
  },
  {
    label: intervals.thirtyMinutes,
    value: '1800',
  },
  {
    label: intervals.fifteenMinutes,
    value: '900',
  },
  {
    label: intervals.fiveMinutes,
    value: '300',
  },
];

interface DestinationCombobox extends ComboboxProps<OptionType> {
  fetchMoreDestinations?: FetchMoreFunction<
    IGetChartTimelineFilters,
    OperationVariables
  >;
  totalDestItems: number;
}

const InfinityScrollCombobox = ({
  fetchMoreDestinations,
  totalDestItems,
  options,
  ...props
}: DestinationCombobox) => {
  const [destinations, setDestinations] = useState(options);
  const [loading, setLoading] = useState(false);

  const onScrollBottom = () => {
    if (destinations.length < totalDestItems && !loading) {
      setLoading(true);
      const remaining = totalDestItems - destinations.length;
      if (fetchMoreDestinations) {
        fetchMoreDestinations({
          variables: {
            dest_offset: destinations.length,
            size: remaining < 10 ? remaining : 10,
          },
          updateQuery(data, { fetchMoreResult }) {
            const newDest =
              fetchMoreResult?.getChartTimelineFilters?.entity_filter?.dest
                ?.dest_entities;
            if (newDest) {
              setDestinations((current) => [
                ...current,
                ...newDest.map((dest) => ({
                  value: dest,
                  label: dest,
                  type: 'dest',
                })),
              ]);
            }
            setLoading(false);
            return data;
          },
        });
      }
    }
  };

  return (
    <Combobox
      options={destinations}
      {...props}
      onMenuScrollBottomCustom={onScrollBottom}
    />
  );
};

/**
 * ApexDetectionsChart - Timeline chart with optional filtering
 *
 * @example Without filters (Entity Modal):
 * <ApexDetectionsChart
 *   showFilters={false}
 *   // No filter props needed when showFilters is false
 * />
 *
 * @example With filters (Detection Details):
 * <ApexDetectionsChart
 *   showFilters={true}
 *   setAlertFilters={setAlertFilters}
 *   setSourceFilters={setSourceFilters}
 *   // All filter handlers required when showFilters is true
 * />
 */

interface ApexDetectionsChartProps {
  // REQUIRED
  end: number;
  start: number;
  maxRows: number;
  interval: number;
  latest_start_timestamp: number;
  setInterval: React.Dispatch<React.SetStateAction<number>>;
  // OPTIONAL
  alertOptions?: OptionType[];
  alertFilters?: OptionType[];
  setAlertFilters?: React.Dispatch<React.SetStateAction<OptionType[]>>;
  severityOptions?: OptionType[];
  severityFilters?: OptionType[];
  setSeverityFilters?: React.Dispatch<React.SetStateAction<OptionType[]>>;
  sourceOptions?: OptionType[];
  sourceFilters?: OptionType[];
  setSourceFilters?: React.Dispatch<React.SetStateAction<OptionType[]>>;
  destOptions?: OptionType[];
  destFilters?: OptionType[];
  setDestFilters?: React.Dispatch<React.SetStateAction<OptionType[]>>;
  totalDestItems?: number;
  sourceDestOptions?: OptionType[];
  sourceDestFilters?: OptionType[];
  setSourceDestFilters?: React.Dispatch<React.SetStateAction<OptionType[]>>;
  chartsData?: IGetDetectionTimelineSummary | IGetEntityTimelineSummary;
  setSelectedDetectionId?: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
  fetchMoreTimelineFilters?: FetchMoreFunction<
    IGetChartTimelineFilters,
    OperationVariables
  >;
  showFilters?: boolean;
  showInfoBanner?: boolean;
  showCurrentDetectionSection?: boolean;
  showSourceDestPairSection?: boolean;
  chartTitle?: string;
}

export const ApexDetectionsChart = React.memo(
  ({
    end,
    start,
    maxRows,
    chartsData,
    interval,
    setInterval,
    alertOptions = [],
    alertFilters = [],
    setAlertFilters = () => undefined,
    sourceOptions = [],
    sourceFilters = [],
    setSourceFilters = () => undefined,
    severityOptions = [],
    severityFilters = [],
    setSeverityFilters = () => undefined,
    destOptions = [],
    destFilters = [],
    setDestFilters = () => undefined,
    totalDestItems = 0,
    sourceDestOptions = [],
    sourceDestFilters = [],
    setSourceDestFilters = () => undefined,
    setSelectedDetectionId = () => undefined,
    latest_start_timestamp,
    fetchMoreTimelineFilters,
    showFilters = true,
    showInfoBanner = true,
    showCurrentDetectionSection = true,
    showSourceDestPairSection = true,
    chartTitle,
  }: ApexDetectionsChartProps) => {
    const { colorMode } = useColorMode();

    const chartWrapper = useRef<HTMLDivElement>(null);
    const [canRenderCharts, setCanRenderCharts] = useState(false);

    const currentDetection = seriesData({
      seriesMaxLength: maxRows,
      seriesData: chartsData?.getCurrentDetectionTimelineSummary,
    });

    const detectionDestination = seriesData({
      seriesMaxLength: maxRows,
      seriesData: chartsData?.getDetectionDestinationTimelineSummary,
    });

    const detectionSource = seriesData({
      seriesMaxLength: maxRows,
      seriesData: chartsData?.getDetectionSourceTimelineSummary,
    });

    const detectionSourceDestination = seriesData({
      seriesMaxLength: maxRows,
      seriesData: chartsData?.getDetectionSourceDestinationTimelineSummary,
    });

    const chartTimelineData: ApexOptions = useMemo(() => {
      return {
        chart: {
          id: 'detection-timeline',
          group: 'timeline-charts',
          type: 'rangeBar',
          toolbar: {
            show: false,
          },
        },
        yaxis: {
          show: false,
          labels: {
            minWidth: 250,
            maxWidth: 250,
          },
        },
        xaxis: {
          type: 'datetime',
          floating: false,
          labels: {
            datetimeUTC: false,
            style: {
              colors: colorMode === 'light' ? '#333333' : '#FFFFFF',
            },
          },
          axisBorder: {
            show: false,
            color: '#525252',
          },
          axisTicks: {
            color: '#525252',
          },
          min: start ? start * 1000 : 0,
          max: end ? end * 1000 : 0,
        },
        legend: {
          show: false,
        },
        grid: {
          show: false,
        },
        tooltip: {
          enabled: false,
        },
        series: [],
      };
    }, [colorMode, start, end]);

    const chartTimelineDataNa = useCallback(
      ({ title }: { title: string }) => {
        return {
          title: {
            floating: true,
            text: title,
            style: {
              color: colorMode === 'light' ? '#393939' : '#C6C6C6',
              fontSize: '14px',
              fontWeight: 600,
            },
          },
          chart: {
            id: `detection-na-${title.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`,
            group: 'timeline-na-charts',
            type: 'rangeBar',
            toolbar: {
              show: false,
            },
          },
          yaxis: {
            show: false,
            labels: {
              minWidth: 250,
              maxWidth: 250,
            },
          },
          xaxis: {
            type: 'datetime',
            labels: {
              show: false,
              style: {
                colors: colorMode === 'light' ? '#333333' : '#FFFFFF',
              },
            },
            axisBorder: {
              show: false,
              color: '#525252',
            },
            axisTicks: {
              show: false,
              color: '#525252',
            },
            min: start,
            max: end,
            title: {
              style: {
                color: colorMode === 'light' ? '#333333' : '#FFFFFF',
              },
            },
          },
          legend: {
            show: false,
          },
          grid: {
            show: false,
          },
          tooltip: {
            enabled: false,
          },
          series: [],
        };
      },
      [colorMode, start, end],
    );

    const detectionChartData: {
      currentDetectionData?: ApexOptions;
      detectionSourceDestinationData?: ApexOptions;
      detectionSourceData?: ApexOptions;
      detectionDestinationData?: ApexOptions;
      chartTimelineData: ApexOptions;
    } = useMemo(() => {
      return {
        ...(currentDetection && {
          currentDetectionData: {
            ...chartOptions({
              maxRows,
              colorMode,
              setSelectedDetectionId,
              start: start ? start * 1000 : 0,
              end: end ? end * 1000 : 0,
              chartIndex: 0,
              chartGroup: 'detections',
              seriesData: currentDetection,
              titleText: headings.currentDetection,
              showToolbar: true,
            }),
          },
        }),
        ...(detectionSourceDestination && {
          detectionSourceDestinationData: {
            ...chartOptions({
              maxRows,
              colorMode,
              setSelectedDetectionId,
              start: start ? start * 1000 : 0,
              end: end ? end * 1000 : 0,
              chartIndex: 1,
              chartGroup: 'detections',
              seriesData: detectionSourceDestination,
              titleText: headings.sourceDest,
              showToolbar: false,
            }),
          },
        }),
        ...(detectionSource && {
          detectionSourceData: {
            ...chartOptions({
              maxRows,
              colorMode,
              setSelectedDetectionId,
              start: start ? start * 1000 : 0,
              end: end ? end * 1000 : 0,
              chartIndex: 2,
              chartGroup: 'detections',
              seriesData: detectionSource,
              titleText: headings.source,
              showToolbar: false,
            }),
          },
        }),
        ...(detectionDestination && {
          detectionDestinationData: {
            ...chartOptions({
              maxRows,
              colorMode,
              setSelectedDetectionId,
              start: start ? start * 1000 : 0,
              end: end ? end * 1000 : 0,
              chartIndex: 3,
              chartGroup: 'detections',
              seriesData: detectionDestination,
              titleText: headings.destination,
              showToolbar: false,
            }),
          },
        }),
        chartTimelineData,
      };
    }, [
      currentDetection,
      maxRows,
      colorMode,
      setSelectedDetectionId,
      start,
      end,
      detectionSourceDestination,
      detectionSource,
      detectionDestination,
      chartTimelineData,
    ]);

    const getBarHeight = (length: number) => {
      if (length === 1) return 70;
      if (length === 2) return length * 50;
      if (length === 3) return length * 45;
      if (length === 4) return length * 40;
      if (length === 5) return length * 35;
      return 0;
    };

    const currentDetectionDataSeries = detectionChartData?.currentDetectionData
      ?.series as ApexAxisChartSeries;
    const detectionSourceDestinationDataSeries = detectionChartData
      ?.detectionSourceDestinationData?.series as ApexAxisChartSeries;
    const detectionSourceDataSeries = detectionChartData?.detectionSourceData
      ?.series as ApexAxisChartSeries;
    const detectionDestinationDataSeries = detectionChartData
      ?.detectionDestinationData?.series as ApexAxisChartSeries;

    const currentDetectionDataLength = currentDetectionDataSeries
      ? currentDetectionDataSeries?.filter((data) => data.name !== '')?.length
      : 0;

    const detectionSourceDestinationDataLength =
      detectionSourceDestinationDataSeries
        ? detectionSourceDestinationDataSeries?.filter(
            (data) => data.name !== '',
          )?.length
        : 0;

    const detectionSourceDataLength = detectionSourceDataSeries
      ? detectionSourceDataSeries?.filter((data) => data.name !== '')?.length
      : 0;

    const detectionDestinationDataLength = detectionDestinationDataSeries
      ? detectionDestinationDataSeries?.filter((data) => data.name !== '')
          ?.length
      : 0;

    const total =
      (detectionDestination?.row_count || 0) +
      (detectionSource?.row_count || 0) +
      (detectionSourceDestination?.row_count || 0);

    const earliestStartDiff = moment(latest_start_timestamp * 1000).diff(
      moment(start * 1000),
      'days',
    );

    const latestEndDiff = moment(end * 1000).diff(
      moment(latest_start_timestamp * 1000),
      'days',
    );

    useLayoutEffect(() => {
      const element = chartWrapper.current;
      if (!element) return;
      const resizeObserver = new window.ResizeObserver(() => {
        // Check if the container has an actual renderable area - related to edge case in Firefox (see INVEST-12493)
        const hasRenderableArea =
          element.offsetWidth > 0 && element.offsetHeight > 0;
        setCanRenderCharts(hasRenderableArea);
      });
      resizeObserver.observe(element);

      return () => resizeObserver.disconnect();
    }, []);

    return (
      <FlushPanelContent>
        <Box
          position="relative"
          css={{
            '#chart-wrapper': {
              containerType: 'inline-size',
              '.chakra-form-control': {
                flexDirection: 'row-reverse',
                label: {
                  marginRight: '8px',
                },
              },
            },
            '.apexcharts-toolbar': {
              marginRight: '8px',
              top: '-37px !important',
            },
            '.apexcharts-tooltip-rangebar .category': {
              color: '#FFFFFF',
            },
            '.apexcharts-pan-icon svg': {
              fill: '#6e8192',
            },
            '.apexcharts-pan-icon.apexcharts-selected svg': {
              fill: '#008ffb',
            },
            '.apexcharts-rangebar-area': {
              cursor: 'pointer',
            },
            'div[type="rangeBar"]': {
              minHeight: '0 !important',
            },
            '.chart-0-tooltip, .chart-1-tooltip, .chart-2-tooltip, .chart-3-tooltip':
              {
                marginTop: '55px',
              },
            '.detection-tooltip': {
              border: 0,
              padding: '6px 12px',
              fontSize: '12px',
              color: '#F4F4F4',
              borderRadius: '6px',
              background: '#393939',
              minWidth: '165px',
              maxWidth: '500px',
              whiteSpace: 'normal',
            },
            '.apexcharts-tooltip.apexcharts-theme-light': {
              border: 'none',
              boxShadow: 'none',
              background: 'transparent',
            },
            '.goal-line': {
              cursor: 'pointer',
            },
            '#tooltip': {
              visibility: 'hidden',
              opacity: 0,
              position: 'fixed',
              zIndex: 100000,
            },
          }}
        >
          <Box padding={4} id="chart-wrapper">
            <Box mb={2} mx={2}>
              {showInfoBanner && (
                <Alert status="info" variant="subtle">
                  {latestEndDiff === 0
                    ? i18n.formatString(
                        abbrevChartDescription,
                        <strong>
                          {moment(start * 1000).format('MMM DD')}
                        </strong>,
                        <strong>{moment(end * 1000).format('MMM DD')}</strong>,
                        <strong>{String(earliestStartDiff)}</strong>,
                        total > 0 ? (
                          <strong>{String(total)}</strong>
                        ) : (
                          <strong>{'NO'}</strong>
                        ),
                      )
                    : i18n.formatString(
                        chartDescription,
                        <strong>
                          {moment(start * 1000).format('MMM DD')}
                        </strong>,
                        <strong>{moment(end * 1000).format('MMM DD')}</strong>,
                        <strong>{String(earliestStartDiff)}</strong>,
                        <strong>{String(latestEndDiff)}</strong>,
                        total > 0 ? (
                          <strong>{String(total)}</strong>
                        ) : (
                          <strong>{'NO'}</strong>
                        ),
                      )}
                </Alert>
              )}
            </Box>
            <Flex
              data-testid="responsive-flex"
              zIndex={10}
              position="relative"
              color="text.secondary"
              justifyContent={
                showFilters || chartTitle ? 'space-between' : 'flex-end'
              }
              direction={{ base: 'column', xl: 'row' }}
              w="100%"
              wrap="wrap"
              css={{
                '@container (min-width: 1200px)': {
                  flexDirection: 'row',
                },
                '@container (max-width: 1199px)': {
                  flexDirection: 'column',
                },
              }}
            >
              {chartTitle && (
                <Box
                  fontSize="lg"
                  fontWeight="600"
                  color="text.primary"
                  mb={{ base: 2, xl: 0 }}
                >
                  {chartTitle}
                </Box>
              )}
              {showFilters && (
                <Wrap
                  data-testid="responsive-wrap"
                  spacing={2}
                  css={{
                    '@container (min-width: 1200px)': {
                      width: '60%',
                      maxWidth: '60%',
                      marginBottom: 0,
                    },
                    '@container (max-width: 1199px)': {
                      width: '100%',
                      maxWidth: '100%',
                      marginBottom: '10px',
                    },
                  }}
                >
                  <Combobox
                    size="sm"
                    category="Source"
                    options={sourceOptions}
                    value={sourceFilters}
                    onChange={setSourceFilters}
                    isDisabled={sourceOptions.length === 0}
                  />
                  <InfinityScrollCombobox
                    category="Destination"
                    options={destOptions}
                    value={destFilters}
                    onChange={setDestFilters}
                    isDisabled={destOptions.length === 0}
                    size="sm"
                    totalDestItems={totalDestItems}
                    fetchMoreDestinations={fetchMoreTimelineFilters}
                  />
                  <Combobox
                    size="sm"
                    category="Source-Dest Pair"
                    options={sourceDestOptions}
                    value={sourceDestFilters}
                    onChange={setSourceDestFilters}
                    isDisabled={sourceDestOptions.length === 0}
                  />
                  <Combobox
                    size="sm"
                    category="Rule"
                    options={alertOptions}
                    value={alertFilters}
                    onChange={setAlertFilters}
                    isDisabled={alertOptions.length === 0}
                  />
                  <Combobox
                    size="sm"
                    category="Severity"
                    options={severityOptions}
                    value={severityFilters}
                    onChange={setSeverityFilters}
                    isDisabled={severityOptions.length === 0}
                  />
                </Wrap>
              )}
              <HStack
                data-testid="responsive-hstack"
                spacing={2}
                css={{
                  '@container (min-width: 1200px)': {
                    position: 'relative',
                    right: '150px',
                    justifyContent: 'flex-end',
                    width: '40%',
                    maxWidth: '40%',
                  },
                  '@container (max-width: 1199px)': {
                    position: 'static',
                    right: 'auto',
                    justifyContent: 'flex-start',
                    width: '100%',
                    maxWidth: '100%',
                  },
                }}
              >
                <Flex mr={2}>
                  <Box mr={2} alignSelf="center">
                    {correlationChart.interval}:
                  </Box>
                  <Select
                    size="sm"
                    inline={true}
                    minWidth="120px"
                    name="interval"
                    label=""
                    aria-label={detectionTimelineInterval}
                    isMulti={false}
                    isMenuPortal={true}
                    options={intervalOptions}
                    value={intervalOptions.find(
                      ({ value }) => value === String(interval),
                    )}
                    onChange={(selected) => {
                      selected && setInterval(Number(selected.value));
                    }}
                  />
                </Flex>
                <CalendarTodaySharpIcon />
                <Text>
                  <span className="date-range-label">
                    {correlationChart.dateRange}:
                  </span>
                  {`${moment(start * 1000).format('MMM D')} - ${moment(
                    end * 1000,
                  ).format('MMM D')}`}
                </Text>
              </HStack>
            </Flex>

            <Box
              mt={4}
              ref={chartWrapper}
              minHeight="200px"
              role="img"
              aria-label={`${chartTitle || detections} ${ariaLabelForTimelineChart}`}
            >
              {canRenderCharts && (
                <>
                  {showCurrentDetectionSection &&
                    currentDetectionDataLength > 0 && (
                      <Box
                        key={`current-${start}-${end}-${interval}-${currentDetectionDataLength}`}
                      >
                        <Chart
                          type="rangeBar"
                          height="70px"
                          series={currentDetectionDataSeries}
                          options={detectionChartData.currentDetectionData}
                        />
                      </Box>
                    )}

                  {showSourceDestPairSection && (
                    <Box
                      key={`src-dst-${start}-${end}-${interval}${detectionSourceDestinationDataLength > 0 ? `-${detectionSourceDestinationDataLength}` : '-na'}`}
                    >
                      {detectionSourceDestinationDataLength > 0 ? (
                        <Chart
                          type="rangeBar"
                          series={detectionSourceDestinationDataSeries}
                          options={
                            detectionChartData.detectionSourceDestinationData
                          }
                          height={`${getBarHeight(detectionSourceDestinationDataLength)}px`}
                        />
                      ) : (
                        <Chart
                          height="25px"
                          type="rangeBar"
                          series={[]}
                          options={
                            chartTimelineDataNa({
                              title: `${headings.sourceDest} (N/A)`,
                            }) as ApexOptions
                          }
                        />
                      )}
                    </Box>
                  )}
                  <Box
                    key={`src-${start}-${end}-${interval}${detectionSourceDataLength > 0 ? `-${detectionSourceDataLength}` : '-na'}`}
                  >
                    {detectionSourceDataLength > 0 ? (
                      <Chart
                        type="rangeBar"
                        series={detectionSourceDataSeries}
                        options={detectionChartData.detectionSourceData}
                        height={`${getBarHeight(detectionSourceDataLength)}px`}
                      />
                    ) : (
                      <Chart
                        height="25px"
                        type="rangeBar"
                        series={[]}
                        options={
                          chartTimelineDataNa({
                            title: `${headings.source} (N/A)`,
                          }) as ApexOptions
                        }
                      />
                    )}
                  </Box>

                  <Box
                    key={`dst-${start}-${end}-${interval}${detectionDestinationDataLength > 0 ? `-${detectionDestinationDataLength}` : '-na'}`}
                  >
                    {detectionDestinationDataLength > 0 ? (
                      <Chart
                        type="rangeBar"
                        series={detectionDestinationDataSeries}
                        options={detectionChartData.detectionDestinationData}
                        height={`${getBarHeight(detectionDestinationDataLength)}px`}
                      />
                    ) : (
                      <Chart
                        height="25px"
                        type="rangeBar"
                        series={[]}
                        options={
                          chartTimelineDataNa({
                            title: `${headings.destination} (N/A)`,
                          }) as ApexOptions
                        }
                      />
                    )}
                  </Box>

                  {detectionChartData?.chartTimelineData && (
                    <Box key={`timeline-${start}-${end}-${interval}`}>
                      <Chart
                        height="50px"
                        type="rangeBar"
                        series={[]}
                        options={detectionChartData.chartTimelineData}
                      />
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Box>
          <Divider bottom="51px" position="absolute" />
          <Box id="tooltip" />
        </Box>
        <style>{`
          @container (max-width: 1200px) {
            .date-range-label {
              display: none !important;
            }
          }
          @container (min-width: 1199px) {
            .date-range-label {
              display: inline !important;
              padding-right: 4px;
            }
          }
          /* Because we are limiting the yaxis label width to 250px now,
             we need to adjust the position of the labels accordingly */
          .apexcharts-yaxis-texts-g {
            transform: translate(-250px, 0px);
          }
        `}</style>
      </FlushPanelContent>
    );
  },
);
